import { useEffect, useState, type ReactNode } from "react";

// BRK-S138 — the debounced server-backed typeahead, extracted.
//
// `ClassCodeField` (BRK-S047/S065) has owned this shape since the WC rating
// form: type at least two characters, wait ~300 ms, search the server, list what
// came back, and once something is chosen show it as a chip with a **Change**
// affordance. The NAICS picker on the Surety account profile is the SECOND field
// that wants all of it, and a repeated block is a smell (CLAUDE.md, Coding
// Standards) — so the shell lives here once and both compose it.
//
// A BEHAVIOR-IDENTICAL EXTRACTION FIRST (the BRK-S067 precedent). Nothing about
// the class-code field's behaviour changes: the same aria-label, the same
// two-character floor, the same 300 ms, the same "Searching…" line, the same
// chip. What moved is only the part both copies would have had to keep in step —
// and the part that would have drifted is the CANCELLATION, which is the half
// nobody looks at until a stale response overwrites a fresh one.
//
// `search` REPORTS ITS OWN EMPTY MESSAGE, which is the one piece of design worth
// naming. The class-code search is SCOPED (to a submission's product and the
// insured's state, PMS-S156), so an empty page there has a reason — "no eligible
// codes for TX on this product", "add the insured's state" — and a bare "no
// results" would be a lie about appetite. Letting the search return the sentence
// alongside the items keeps that knowledge in the caller that owns the endpoint,
// with no reason-shaped state in this component and no second render pass.
//
// ⚠️ `search` MUST BE REFERENTIALLY STABLE. It is an effect dependency, so a
// fresh closure per render restarts the debounce on every render — which is an
// unbounded request loop, not a slow one. Wrap it in `useCallback`.

/** What one search resolved to: the rows, and — when there are none — why. */
export interface TypeaheadResult<T> {
  items: T[];
  /**
   * What to say when `items` is empty. Optional: absent falls back to the
   * generic line, which is the honest answer for a plain search miss.
   */
  emptyMessage?: ReactNode;
}

export function Typeahead<T>({
  searchLabel,
  placeholder,
  search,
  selected,
  onSelect,
  keyOf,
  renderOption,
  renderSelected,
  changeLabel = "Change",
  minChars = 2,
  debounceMs = 300,
}: {
  /** The input's accessible name — it has no visible label of its own. */
  searchLabel: string;
  placeholder?: string;
  /** MUST be stable (see the header). Resolves the rows and any empty line. */
  search: (query: string) => Promise<TypeaheadResult<T>>;
  selected: T | null;
  /** `null` clears the selection — the Change affordance. */
  onSelect: (item: T | null) => void;
  keyOf: (item: T) => string;
  renderOption: (item: T) => ReactNode;
  renderSelected: (item: T) => ReactNode;
  changeLabel?: string;
  minChars?: number;
  debounceMs?: number;
}) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<TypeaheadResult<T> | null>(null);
  const [searching, setSearching] = useState(false);

  // Debounced, and only while nothing is selected: a chosen value is not a
  // search in progress, and searching behind one would race the chip.
  useEffect(() => {
    if (selected) return;
    const q = query.trim();
    if (q.length < minChars) {
      setResult(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const next = await search(q);
        // A LATE response for a query the broker has moved past must not
        // overwrite the current one — the whole reason the flag is checked
        // after the await and not only before it.
        if (!cancelled) setResult(next);
      } catch {
        // A failed search is not fatal (NN #7): the broker sees "no results"
        // and can keep typing, rather than an error they cannot act on.
        if (!cancelled) setResult({ items: [] });
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, selected, search, minChars, debounceMs]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm">
        <span className="min-w-0 text-brk-text">
          {renderSelected(selected)}
        </span>
        <button
          type="button"
          className="shrink-0 text-brk-accent hover:underline"
          onClick={() => {
            onSelect(null);
            setQuery("");
            setResult(null);
          }}
        >
          {changeLabel}
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        type="search"
        aria-label={searchLabel}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
      />
      {searching && (
        <p role="status" className="text-xs text-brk-dim">
          Searching…
        </p>
      )}
      {result && result.items.length === 0 && !searching && (
        <p className="text-xs text-brk-dim">
          {result.emptyMessage ?? "No results."}
        </p>
      )}
      {result && result.items.length > 0 && (
        <ul className="flex flex-col overflow-hidden rounded-md border border-brk-border">
          {result.items.map((item) => (
            <li
              key={keyOf(item)}
              className="border-t border-brk-border first:border-t-0"
            >
              <button
                type="button"
                onClick={() => {
                  onSelect(item);
                  setResult(null);
                }}
                className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-brk-bg"
              >
                {renderOption(item)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
